import {
  ASSET_STATUS_LABELS,
  type AssetListQuery,
  type AssetLookups,
  type AssetStatus,
} from '@gb-ticket/shared-types';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type AssetsFiltersProps = {
  query: AssetListQuery;
  lookups: AssetLookups | undefined;
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  onChange: (patch: Partial<AssetListQuery>) => void;
};

export function AssetsFilters({
  query,
  lookups,
  searchInput,
  onSearchInputChange,
  onChange,
}: AssetsFiltersProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
      <div className="space-y-1.5 sm:col-span-2 lg:col-span-1">
        <Label htmlFor="asset-search">Suche</Label>
        <Input
          id="asset-search"
          value={searchInput}
          onChange={(event) => onSearchInputChange(event.target.value)}
          placeholder="Name, Inventar, Serie, Standort"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="asset-type">Typ</Label>
        <Select
          value={query.assetTypeId ?? 'all'}
          onValueChange={(value) =>
            onChange({ assetTypeId: value === 'all' ? undefined : value, page: 1 })
          }
        >
          <SelectTrigger id="asset-type" className="w-full">
            <SelectValue placeholder="Alle Typen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Typen</SelectItem>
            {(lookups?.types ?? []).map((type) => (
              <SelectItem key={type.id} value={type.id}>
                {type.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="asset-status">Status</Label>
        <Select
          value={query.status ?? 'all'}
          onValueChange={(value) =>
            onChange({ status: value === 'all' ? undefined : (value as AssetStatus), page: 1 })
          }
        >
          <SelectTrigger id="asset-status" className="w-full">
            <SelectValue placeholder="Alle Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            {(
              lookups?.statuses ??
              Object.entries(ASSET_STATUS_LABELS).map(([code, label]) => ({ code, label }))
            ).map((item) => (
              <SelectItem key={item.code} value={item.code}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="asset-department">Abteilung</Label>
        <Select
          value={query.departmentId ?? 'all'}
          onValueChange={(value) =>
            onChange({ departmentId: value === 'all' ? undefined : value, page: 1 })
          }
        >
          <SelectTrigger id="asset-department" className="w-full">
            <SelectValue placeholder="Alle Abteilungen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Abteilungen</SelectItem>
            {(lookups?.departments ?? []).map((department) => (
              <SelectItem key={department.id} value={department.id}>
                {department.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="asset-user">Nutzer</Label>
        <Select
          value={query.assignedUserId ?? 'all'}
          onValueChange={(value) =>
            onChange({ assignedUserId: value === 'all' ? undefined : value, page: 1 })
          }
        >
          <SelectTrigger id="asset-user" className="w-full">
            <SelectValue placeholder="Alle Nutzer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Nutzer</SelectItem>
            {(lookups?.users ?? []).map((user) => (
              <SelectItem key={user.id} value={user.id}>
                {user.displayName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
